import React, { useMemo, useState } from 'react';
import QrCode from '../../components/shared/QrCode';
import { getRegisterUrl } from '../../utils/portalConfig';
import { addAdFs, removeAdFs } from '../../utils/firestoreStore';
import { useData } from '../../context/DataContext';

const REGISTER_QR_VALUE = 'https://schoolofshem.com/register';

const defaultCampaign = {
  name: 'February Intake - Kingdom Economics',
  startDate: '2026-02-07',
  price: 'R14,175',
  tagline: 'Become an economic gatekeeper in 2026.',
  link: '/register'
};

const buildFlyerSvg = (campaign) => `
<svg xmlns="http://www.w3.org/2000/svg" width="900" height="1200" viewBox="0 0 900 1200">
  <rect width="900" height="1200" fill="#1B0B3F"/>
  <rect x="60" y="80" width="780" height="1040" rx="24" fill="#FFFFFF"/>
  <text x="120" y="180" fill="#1B0B3F" font-size="42" font-family="Georgia, serif" font-weight="700">School of SHEM</text>
  <text x="120" y="260" fill="#1B0B3F" font-size="32" font-family="Arial, sans-serif" font-weight="600">${campaign.name}</text>
  <text x="120" y="320" fill="#4B5563" font-size="20" font-family="Arial, sans-serif">${campaign.tagline}</text>
  <text x="120" y="420" fill="#111827" font-size="24" font-family="Arial, sans-serif">Start date: ${campaign.startDate}</text>
  <text x="120" y="460" fill="#111827" font-size="24" font-family="Arial, sans-serif">Price: ${campaign.price}</text>
  ${campaign.image ? `<image href="${campaign.image}" x="120" y="520" width="660" height="280" preserveAspectRatio="xMidYMid slice" />` : '<rect x="120" y="520" width="660" height="280" rx="16" fill="#F3F4F6"/>'}
  <text x="120" y="900" fill="#1B0B3F" font-size="18" font-family="Arial, sans-serif">Register at ${campaign.link}</text>
</svg>
`;

export default function AdminAssets() {
  const { ads = [] } = useData() || {};
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [campaign, setCampaign] = useState(defaultCampaign);
  const [campaignImage, setCampaignImage] = useState('');
  const [adFile, setAdFile] = useState(null);
  const [adUploading, setAdUploading] = useState(false);
  const [adError, setAdError] = useState('');
  const registerUrl = getRegisterUrl();

  const outputs = useMemo(() => {
    const link = registerUrl;
    return {
      flyer: {
        title: campaign.name,
        startDate: campaign.startDate,
        price: campaign.price,
        tagline: campaign.tagline,
        link
      },
      scripts: [
        `Hook: "${campaign.tagline}"\nBody: Invite viewers to join ${campaign.name} starting ${campaign.startDate}.\nCTA: Register at ${link}.`,
        `Hook: "Registration is open."\nBody: Share 3 outcomes from ${campaign.name} and highlight the start date.\nCTA: Secure your seat at ${link}.`,
        `Hook: "Take your stewardship to the next level."\nBody: Explain the value + price (${campaign.price}).\nCTA: Register now at ${link}.`
      ],
      captions: [
        `${campaign.name} starts ${campaign.startDate}. ${campaign.tagline} Register now: ${link}`,
        `Your February intake is here. ${campaign.name} • ${campaign.price}. Register: ${link}`,
        `Seats are filling up! ${campaign.name} starts ${campaign.startDate}. ${link}`
      ],
      hashtags: '#SchoolOfSHEM #KingdomEconomics #BiblicalStewardship #ChristianEducation #WealthTransfer'
    };
  }, [campaign, registerUrl]);

  const handleCopy = async (text) => {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    }
  };

  const handleDownloadFlyer = () => {
    const svg = buildFlyerSvg({
      name: campaign.name,
      startDate: campaign.startDate,
      price: campaign.price,
      tagline: campaign.tagline,
      link: registerUrl,
      image: campaignImage
    });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'shem-flyer.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCampaignImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCampaignImage(reader.result?.toString() || '');
    };
    reader.readAsDataURL(file);
  };

  const handleAdUpload = async () => {
    if (!useFirebase) return;
    if (!adFile) {
      setAdError('Please choose an image or video file to upload.');
      return;
    }
    setAdError('');
    setAdUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const ad = {
        id: `ad-${Date.now()}`,
        name: adFile.name,
        type: adFile.type.startsWith('video') ? 'video' : 'image',
        url: reader.result?.toString() || '',
        createdAt: new Date().toISOString()
      };
      addAdFs(ad);
      setAdFile(null);
      setAdUploading(false);
    };
    reader.onerror = () => {
      setAdError('Upload failed. Please try again.');
      setAdUploading(false);
    };
    reader.readAsDataURL(adFile);
  };

  const handleRemoveAd = (adId) => {
    if (!useFirebase) return;
    removeAdFs(adId);
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Marketing Assets</h1>
        <p className="page-subtitle">Generate flyers, scripts, captions, and QR codes for campaigns.</p>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1rem' }}>Create Campaign</h3>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Course/Event name</label>
              <input className="form-input" value={campaign.name} onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Start date</label>
              <input className="form-input" value={campaign.startDate} onChange={(e) => setCampaign(prev => ({ ...prev, startDate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Price</label>
              <input className="form-input" value={campaign.price} onChange={(e) => setCampaign(prev => ({ ...prev, price: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Short tagline (hook)</label>
              <input className="form-input" value={campaign.tagline} onChange={(e) => setCampaign(prev => ({ ...prev, tagline: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Link to use</label>
              <input className="form-input" value={registerUrl} readOnly />
            </div>
            <div className="form-group">
              <label className="form-label">Upload flyer image</label>
              <input className="form-input" type="file" accept="image/*" onChange={handleCampaignImageUpload} />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1rem' }}>Flyer Preview</h3>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
            <div style={{ borderRadius: '16px', padding: '20px', background: 'var(--primary-dark)', color: 'white' }}>
              <div style={{ fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase' }}>School of SHEM</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '6px' }}>{outputs.flyer.title}</div>
              <div style={{ marginTop: '8px', fontSize: '0.875rem', opacity: 0.85 }}>{outputs.flyer.tagline}</div>
              <div style={{ marginTop: '12px', fontSize: '0.875rem' }}>Starts {outputs.flyer.startDate}</div>
              <div style={{ fontWeight: 600, marginTop: '4px' }}>{outputs.flyer.price}</div>
              <div style={{ marginTop: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.1)', padding: '12px', textAlign: 'center' }}>
                {campaignImage ? (
                  <img src={campaignImage} alt="Campaign" style={{ maxWidth: '100%', borderRadius: '8px' }} />
                ) : (
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>Flyer image placeholder</div>
                )}
              </div>
            </div>
            <button className="btn btn-outline" onClick={handleDownloadFlyer}>Download SVG</button>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1rem' }}>Video Script Prompts</h3>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
            {outputs.scripts.map((script, i) => (
              <div key={i} style={{ padding: '12px', background: 'var(--gray-50)', borderRadius: '10px' }}>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.875rem' }}>{script}</pre>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1rem' }}>Captions & Hashtags</h3>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
            {outputs.captions.map((caption, i) => (
              <div key={i} style={{ padding: '12px', background: 'var(--gray-50)', borderRadius: '10px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{caption}</p>
                <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(caption)}>Copy caption</button>
              </div>
            ))}
            <div style={{ padding: '12px', background: 'var(--gray-50)', borderRadius: '10px' }}>
              <div style={{ fontSize: '0.875rem' }}>{outputs.hashtags}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(outputs.hashtags)}>Copy hashtags</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 style={{ fontSize: '1rem' }}>QR + Link</h3>
        </div>
        <div className="card-body" style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
          <QrCode value={REGISTER_QR_VALUE} size={140} />
          <div>
            <div style={{ fontWeight: 600 }}>Registration Link</div>
            <div style={{ color: 'var(--gray-500)' }}>{registerUrl}</div>
            <button className="btn btn-ghost btn-sm" onClick={() => handleCopy(registerUrl)}>Copy link</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1rem' }}>Student & Teacher Ad Library</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
          <div className="form-group">
            <label className="form-label">Upload ad (image or video)</label>
            <input
              className="form-input"
              type="file"
              accept="image/*,video/*"
              onChange={(event) => setAdFile(event.target.files?.[0] || null)}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '6px' }}>
              Uploaded ads appear in the student and teacher dashboards.
            </div>
            {adError && <div className="badge badge-danger" style={{ marginTop: '8px' }}>{adError}</div>}
            <button className="btn btn-primary btn-sm" style={{ marginTop: '12px' }} onClick={handleAdUpload} disabled={adUploading}>
              {adUploading ? 'Uploading…' : 'Upload Ad'}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            {ads.length === 0 && (
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                No ads uploaded yet.
              </div>
            )}
            {ads.map(ad => (
              <div key={ad.id} style={{ border: '1px solid var(--gray-100)', borderRadius: '12px', padding: '12px', display: 'grid', gap: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{ad.name}</div>
                {ad.type === 'video' ? (
                  <video src={ad.url} controls style={{ width: '100%', borderRadius: '8px' }} />
                ) : (
                  <img src={ad.url} alt={ad.name} style={{ width: '100%', borderRadius: '8px' }} />
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveAd(ad.id)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
